// SidebarContent.js
import React from "react";
import { Flex, Box, Stack, Tooltip, HStack, VStack, Text, Switch, Divider, CloseButton, Badge } from "@chakra-ui/react";
import { NavGroup } from "./NavGroup";
import { NavItem } from "./NavItem";
import AccountSelector from "../../../components/AccountSelector/AccountSelector";
import { ToggleColorMode } from "./ToggleColorMode";
import { useAtom } from "jotai";
import { ViewModeAtom, setViewModeInStorage } from "../../../store";

export const SidebarContent = ({
  isCollapsed,
  toggleSidebar,
  navigate,
  navigationGroups,
}: {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  navigate: (path: string) => void;
  navigationGroups: any;
}) => {
  const [viewMode, setViewMode] = useAtom(ViewModeAtom);
  const [showAdvancedTooltip, setShowAdvancedTooltip] = React.useState(() => {
    const dismissed = localStorage.getItem('advancedTooltipDismissed');
    return dismissed !== 'true';
  });

  const handleSetMode = (mode: "simple" | "advanced") => {
    setViewMode(mode);
    setViewModeInStorage(mode);
  };

  const dismissAdvancedTooltip = () => {
    setShowAdvancedTooltip(false);
    localStorage.setItem('advancedTooltipDismissed', 'true');
  };
  return (
    <Flex
      h="full"
      direction="column"
      px={isCollapsed ? "2" : "4"}
      justifyContent="center"
      bg="var(--bg)"
      backdropFilter="blur(7.5px)"
      borderRight="1px solid rgba(255, 255, 255, 0.18)"
    >
      <Stack
        spacing="4"
        flex="1"
        overflow="hidden"
        pt="2"
        alignItems={isCollapsed ? "center" : "flex-start"}
        w="full"
        h="full"
      >
        <Tooltip
          label="Account Selector"
          placement="right"
          hasArrow
          isDisabled={!isCollapsed}
        >
          <Box
            display={{ base: "flex", md: "flex" }}
            alignSelf="flex-start"
            maxW={isCollapsed ? "62px" : "100%"}
            overflow="visible"
            transform={isCollapsed ? "translateX(-4px)" : "0"}
          >
            <AccountSelector />
          </Box>
        </Tooltip>

        {navigationGroups.map((group: any, groupIndex: number) => (
          <React.Fragment key={groupIndex}>
            {groupIndex > 0 && (
              <Divider
                borderColor="rgba(255, 255, 255, 0.08)"
                my="2"
              />
            )}
            <NavGroup label={isCollapsed ? "" : group.label}>
              {group.items.map((item: any, itemIndex: number) => {
                const active = item.path && window.location.pathname.startsWith(item.path);
                const handleClick = () => {
                  if (item.external && item.url) {
                    window.open(item.url, "_blank");
                  } else if (item.path) {
                    navigate(item.path);
                  }
                };
                
                const navItem = (
                  <NavItem
                    key={itemIndex}
                    active={active}
                    icon={<item.icon />}
                    isExternal={item.external}
                    label={isCollapsed ? "" : item.label}
                    onClick={handleClick}
                    endElement={item.badge && !isCollapsed ? (
                      <Badge
                        colorScheme="purple"
                        fontSize="2xs"
                        px="1.5"
                        py="0.5"
                        borderRadius="full"
                        textTransform="uppercase"
                        fontWeight="bold"
                      >
                        {item.badge}
                      </Badge>
                    ) : undefined}
                  />
                );

                // Wrap in tooltip when collapsed
                if (isCollapsed) {
                  return (
                    <Tooltip
                      key={itemIndex}
                      label={item.label}
                      placement="right"
                      hasArrow
                    >
                      {navItem}
                    </Tooltip>
                  );
                }

                return navItem;
              })}
            </NavGroup>
          </React.Fragment>
        ))}

        <NavGroup label={isCollapsed ? "" : ""}>
          <ToggleColorMode />
        </NavGroup>
      </Stack>

      {/* View Mode Toggle at bottom */}
      <Box 
        px={isCollapsed ? "2" : "4"} 
        py="3" 
        borderTop="1px solid rgba(255, 255, 255, 0.08)"
        backdropFilter="blur(4px)"
        transition="all 0.2s ease"
       
      >
        {/* Advanced Mode Tooltip */}
        {!isCollapsed && showAdvancedTooltip && (
          <Box
            mb="3"
            bg="rgba(59, 130, 246, 0.15)"
            borderRadius="md"
            p="3"
            border="1px solid rgba(59, 130, 246, 0.3)"
            position="relative"
          >
            <CloseButton
              size="sm"
              position="absolute"
              top="1"
              right="1"
              onClick={dismissAdvancedTooltip}
              color="rgba(255, 255, 255, 0.7)"
              _hover={{
                bg: "rgba(255, 255, 255, 0.1)",
                color: "white"
              }}
            />
            <Text fontSize="xs" fontWeight="semibold" mb="1" color="white">
              Advanced Mode (Free)
            </Text>
            <Text fontSize="2xs" color="rgba(255, 255, 255, 0.8)" lineHeight="1.4">
              Unlock Calendar, Positions, Cashflows & Prices features for comprehensive portfolio tracking
            </Text>
          </Box>
        )}
        
        {!isCollapsed ? (
          <Box
            position="relative"
            w="full"
            bg="rgba(255, 255, 255, 0.05)"
            borderRadius="lg"
            p="1px"
            border="1px solid rgba(255, 255, 255, 0.1)"
          >
            {/* Background slider */}
            <Box
              position="absolute"
              top="1px"
              left={viewMode === "simple" ? "1px" : "50%"}
              w="calc(50% - 1px)"
              h="calc(100% - 2px)"
              bg="var(--accent)"
              borderRadius="md"
              transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
              boxShadow="0 2px 8px rgba(0, 0, 0, 0.3)"
            />
            
            <HStack spacing="0" position="relative" zIndex="1"
            
              id="advanced-view-mode-toggle"
            >
              <Box
                flex="1"
                py="1"
                textAlign="center"
                cursor="pointer"
                onClick={() => handleSetMode("simple")}
                transition="all 0.2s ease"
                borderRadius="md"
                _hover={viewMode !== "simple" ? {
                  bg: "rgba(255, 255, 255, 0.05)"
                } : {}}
              >
                <Text 
                  fontSize="xs" 
                  color={viewMode === "simple" ? "white" : "var(--white)"}
                  fontWeight={viewMode === "simple" ? "bold" : "medium"}
                  transition="all 0.2s ease"
                >
                  Simple
                </Text>
              </Box>
              
              <Box
                flex="1"
                py="1"
                textAlign="center"
                cursor="pointer"
                onClick={() => handleSetMode("advanced")}
                transition="all 0.2s ease"
                borderRadius="md"
                _hover={viewMode !== "advanced" ? {
                  bg: "rgba(255, 255, 255, 0.05)"
                } : {}}
              >
                <Text 
                  fontSize="xs" 
                  color={viewMode === "advanced" ? "white" : "var(--white)"}
                  fontWeight={viewMode === "advanced" ? "bold" : "medium"}
                  transition="all 0.2s ease"
                >
                  Advanced
                </Text>
              </Box>
            </HStack>
          </Box>
        ) : (
          <Tooltip
            label={`Switch to ${viewMode === "advanced" ? "Simple" : "Advanced"} View`}
            placement="right"
            hasArrow
            bg="var(--bg)"
            color="white"
            fontSize="xs"
          >
            <Box
              position="relative"
              w="32px"
              h="18px"
              bg="rgba(255, 255, 255, 0.1)"
              borderRadius="full"
              cursor="pointer"
              onClick={() => handleSetMode(viewMode === "simple" ? "advanced" : "simple")}
              transition="all 0.2s ease"
              border="1px solid rgba(255, 255, 255, 0.2)"
              _hover={{
                bg: "rgba(255, 255, 255, 0.15)",
                transform: "scale(1.05)",
              }}
            >
              <Box
                position="absolute"
                top="1px"
                left={viewMode === "advanced" ? "15px" : "1px"}
                w="16px"
                h="16px"
                bg="var(--accent)"
                borderRadius="full"
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Text fontSize="8px" color="white" fontWeight="bold">
                  {viewMode === "simple" ? "S" : "A"}
                </Text>
              </Box>
            </Box>
          </Tooltip>
        )}
      </Box>

      <Tooltip
        label={isCollapsed ? "Expand" : "Collapse"}
        placement="right"
        hasArrow
      >
        <Flex
          h="full"
          position="absolute"
          right="0px"
          w="4px"
          display="flex"
          alignItems="center"
          onClick={toggleSidebar}
          zIndex={99999}
          cursor="pointer"
          borderRight="1px solid var(--gray)"
          borderLeft="1px solid var(--gray)"
        />
      </Tooltip>
    </Flex>
  );
};
